/**
 * Palette resolution — the single code path for all three coloring tiers.
 *
 * The contract's `plot.palette` expresses three tiers behind one shape:
 *   1. simple  — `color`
 *   2. paired  — `upColor` / `downColor` + `colorMode` ("sign" | "delta")
 *   3. colorBy — a per-bar color expression (overrides the paired colors)
 *
 * They all collapse to a single `PalettePlan` here, and `resolveBarColorsArgb`
 * generalizes qvOrderflow's `DeltaPaletteProvider` into one function every
 * platform adapter can mirror. Tier 3 (`colorBy`) is an expression evaluated
 * upstream (DSL compile / server) whose per-bar colors arrive precomputed, so
 * it is represented but not evaluated here.
 */

import {
  resolveColorRef,
  DEFAULT_THEME,
  type PlotPalette,
  type Theme,
} from "@qufolio/model";
import { parseColorToArgb } from "./color.js";

export type PalettePlan =
  | { kind: "none" }
  | { kind: "solid"; color: string }
  | {
      kind: "paired";
      up: string;
      down: string;
      mode: "sign" | "delta" | "quad";
      /** `quad` only: above zero and falling, below zero and rising. */
      upFade?: string;
      downFade?: string;
    }
  | { kind: "expression"; expr: string };

/** Normalize a contract palette (any tier) into a single resolved plan. */
export function resolvePalettePlan(
  palette: PlotPalette | undefined,
  theme: Theme = DEFAULT_THEME,
): PalettePlan {
  if (!palette) return { kind: "none" };

  // Tier 3 overrides paired colors when present (arch: colorBy wins).
  if (palette.colorBy) return { kind: "expression", expr: palette.colorBy };

  if (palette.upColor || palette.downColor) {
    const up = resolveColorRef(palette.upColor, theme) ?? resolveColorRef("positive", theme) ?? "#26a69a";
    const down = resolveColorRef(palette.downColor, theme) ?? resolveColorRef("negative", theme) ?? "#ef5350";
    if (palette.colorMode === "quad") {
      const upFade = resolveColorRef(palette.upFadeColor, theme) ?? resolveColorRef("series1", theme) ?? up;
      const downFade = resolveColorRef(palette.downFadeColor, theme) ?? resolveColorRef("series3", theme) ?? down;
      return { kind: "paired", up, down, mode: "quad", upFade, downFade };
    }
    const mode = palette.colorMode === "delta" ? "delta" : "sign";
    return { kind: "paired", up, down, mode };
  }

  if (palette.color) {
    const color = resolveColorRef(palette.color, theme);
    if (color) return { kind: "solid", color };
  }

  return { kind: "none" };
}

/**
 * Compute per-bar ARGB colors for a solid/paired plan.
 *
 * - solid:  every bar the same color.
 * - paired: "sign"  → up when value ≥ 0; "delta" → up when value > previous bar
 *           (unchanged is down; the first bar is treated as up).
 * - none / expression: returns an empty array (no per-bar override to apply).
 */
export function resolveBarColorsArgb(
  values: readonly number[],
  plan: PalettePlan,
): number[] {
  switch (plan.kind) {
    case "solid": {
      const c = parseColorToArgb(plan.color);
      return values.map(() => c);
    }
    case "paired": {
      const up = parseColorToArgb(plan.up);
      const down = parseColorToArgb(plan.down);
      if (plan.mode === "quad") {
        // The four-colour histogram (owner, 2026-10-06): which side of zero,
        // and which way since the bar before. The first bar has nothing before
        // it and counts as rising — the strong colour for its side.
        const upFade = parseColorToArgb(plan.upFade ?? plan.up);
        const downFade = parseColorToArgb(plan.downFade ?? plan.down);
        return values.map((v, i) => {
          const rising = i === 0 || v > values[i - 1]!;
          return v >= 0 ? (rising ? up : upFade) : rising ? downFade : down;
        });
      }
      return values.map((v, i) => {
        // `delta`: up only when it rose — `close > close[1]` (owner,
        // 2026-10-03, for the two-colour price line); unchanged is down. The
        // first bar has nothing before it and stays up.
        const isUp = plan.mode === "delta" ? i === 0 || v > values[i - 1]! : v >= 0;
        return isUp ? up : down;
      });
    }
    default:
      return [];
  }
}